import BaseEllipsisMenu from 'components/BaseEllipsisMenu/BaseEllipsisMenu';
import BaseTable from 'components/Tables/BaseTable';
import TextToggle from 'components/UI/TextToggle';
import { usePerms } from 'contexts/PermsContext';
import { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { MdPushPin } from 'react-icons/md';
import { RiAlertFill } from 'react-icons/ri';
import Popup from 'reactjs-popup';
import { useGetDevicesByIdsQuery } from 'services/devices/devices.api';
import { useGetGlobalActionsQuery } from 'services/globalActions/globalActions.api';
import { capitalizeWord } from 'utils/strings/string.utils';
import {
  getRelatedDevicesFromGlobalAction,
  isMissingDevice,
  shouldHideGlobalActionFromList,
} from '../utils/actionListUtils';
import { formatGlobalActionRecentActivity } from '../utils/activityUtils';

const DEFAULT_COLUMN_ORDER = ['id', 'state', 'title', 'action', 'recentActivity', 'status'];
const DEFAULT_COLUMN_VISIBILITY = {
  id: false,
  recentActivity: false,
};

const Table = ({ onView, onEdit, onRemove, enableGlobalAction, searchFilter }) => {
  const { data: globalActions = [] } = useGetGlobalActionsQuery();

  // Extract all device IDs referenced by the current global actions
  const deviceIds = useMemo(() => {
    const ids = new Set();
    globalActions.forEach((action) => {
      getRelatedDevicesFromGlobalAction(action).forEach((id) => ids.add(id));
    });
    const arr = Array.from(ids);
    return arr;
  }, [globalActions]);

  // Only fetch devices that are referenced in the table
  const { data: deviceList = [], isLoading: isDeviceListLoading } = useGetDevicesByIdsQuery(
    deviceIds,
    {
      skip: deviceIds.length === 0,
    },
  );

  const perms = usePerms();
  const [rowIdEllipsisOpen, setRowIdEllipsisOpen] = useState({});

  const columns = useMemo(() => {
    return [
      {
        header: 'ID',
        accessorKey: 'id',
        Cell: ({ cell }) => {
          const id = cell.getValue();
          const row = cell.row.original;
          return (
            <div className="flex-row">
              <div className="text-white fs-6">{id}</div>
              {/* The following popup only appears if there are devices missing from the device list */}
              {!isDeviceListLoading && isMissingDevice(row.item, deviceList) && (
                <div className="ms-2">
                  <Popup
                    on="hover"
                    position={'top center'}
                    trigger={
                      <div>
                        <RiAlertFill color="yellow" size={20} />
                      </div>
                    }
                  >
                    <div className="text-white p-2 rounded-2 bg-primary-shadow">Missing Device</div>
                  </Popup>
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: 'Current State',
        accessorKey: 'state',
        Cell: ({ cell }) => {
          const state = cell.getValue();
          return <div className="text-white fw-bold fs-6">{state}</div>;
        },
      },
      {
        header: 'Title',
        accessorKey: 'title',
        Cell: ({ cell }) => {
          const title = cell.getValue();
          return (
            <Popup
              on="hover"
              position={'top center'}
              trigger={<div className="text-white">{title}</div>}
            >
              <div className="text-white p-2 rounded-2 bg-primary-shadow">{title}</div>
            </Popup>
          );
        },
      },
      {
        header: 'Action',
        accessorKey: 'action',
        Cell: ({ cell }) => {
          const action = cell.getValue();
          return <div className="text-white fs-6">{action}</div>;
        },
      },
      {
        header: 'Recent Activity',
        accessorKey: 'recentActivity',
        sortingFn: (rowA, rowB) => {
          const timestampA = rowA.original.recentActivityTime ?? 0;
          const timestampB = rowB.original.recentActivityTime ?? 0;
          return timestampA - timestampB;
        },
        Cell: ({ cell }) => {
          const recentActivity = cell.getValue();
          return <div className="text-white fs-6">{recentActivity}</div>;
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        enableHiding: false,
        Cell: ({ row, cell }) => {
          const status = cell.getValue();
          const id = row.original.id;
          const rowPinning = row.original.__baseTableRowPinning;
          const ellipsisOptions = [
            rowPinning && {
              component: (
                <Button
                  className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                  onClick={() => {
                    rowPinning.toggle();
                    setRowIdEllipsisOpen({
                      ...rowIdEllipsisOpen,
                      [id]: false,
                    });
                  }}
                  variant="secondary"
                >
                  {rowPinning.isPinned ? 'Unpin' : 'Pin to top'}
                </Button>
              ),
            },
            {
              component: (
                <>
                  {perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewGlobalActions']) && (
                    <Button
                      className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                      onClick={() => onView(row.original.item)}
                      variant="secondary"
                    >
                      View
                    </Button>
                  )}
                </>
              ),
            },
            {
              component: (
                <>
                  {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditGlobalActions']) && (
                    <Button
                      className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                      onClick={() => onEdit(row.original.item)}
                      variant="secondary"
                    >
                      Edit
                    </Button>
                  )}
                </>
              ),
            },
            {
              component: (
                <>
                  {perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteGlobalActions']) && (
                    <Button
                      className="w-100 px-5 text-center cursor-pointer text-white border border-1 border-black"
                      onClick={() => onRemove(row.original.item)}
                      variant="danger"
                    >
                      Remove
                    </Button>
                  )}
                </>
              ),
            },
          ].filter(Boolean);

          return (
            <div className="d-flex align-items-center gap-3">
              {rowPinning?.isPinned && <MdPushPin className="text-white flex-shrink-0" />}
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Enable-DisableGlobalActions']) ? (
                <TextToggle
                  options={['Offline', 'Online']}
                  value={status}
                  setValue={(value) => enableGlobalAction(id, value)}
                  parentControlledState
                />
              ) : (
                <div
                  className="p-1 text-center rounded-3 text-white"
                  style={{
                    backgroundColor: status ? '#01B574' : '',
                    border: status ? '0px' : '1px solid #ccc',
                    width: 80,
                  }}
                >
                  {status ? 'Online' : 'Offline'}
                </div>
              )}
              <BaseEllipsisMenu
                options={ellipsisOptions}
                open={rowIdEllipsisOpen[id]}
                onOpen={() =>
                  setRowIdEllipsisOpen({
                    ...rowIdEllipsisOpen,
                    [id]: true,
                  })
                }
                onClose={() =>
                  setRowIdEllipsisOpen({
                    ...rowIdEllipsisOpen,
                    [id]: false,
                  })
                }
                closeOnScroll={true}
              />
            </div>
          );
        },
        size: 250,
      },
    ];
  }, [
    onEdit,
    onRemove,
    onView,
    enableGlobalAction,
    rowIdEllipsisOpen,
    perms,
    deviceList,
    isDeviceListLoading,
  ]);

  const rows = useMemo(() => {
    if (globalActions.length === 0) return [];

    return globalActions
      .filter((item) => !shouldHideGlobalActionFromList(item))
      .filter(searchFilter)
      .map((item, index) => ({
        id: item.id,
        state: capitalizeWord(item.state),
        title: item.title,
        action: item.actionName,
        recentActivity: formatGlobalActionRecentActivity(item),
        recentActivityTime: new Date(item?.updated_time ?? item?.created_time ?? 0).getTime() || 0,
        status: item.status,
        item: item,
      }));
  }, [globalActions, searchFilter]);

  return (
    <BaseTable
      customStyles={{ cell: { borderBottom: '1px solid #56577A' } }}
      columns={columns}
      rows={rows}
      enableRowPinning
      tablePreferenceKey="global-actions-action-list"
      initialState={{
        columnVisibility: DEFAULT_COLUMN_VISIBILITY,
        columnOrder: DEFAULT_COLUMN_ORDER,
      }}
      muiTablePaperProps={{
        sx: {
          height: 'auto',
          maxHeight: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      muiTableContainerProps={{
        sx: {
          flex: '0 1 auto',
          minHeight: 0,
          maxHeight: '100%',
          overflowY: 'auto',
        },
      }}
    />
  );
};

export default Table;
